/* ----------------------------------------------------------------------------
 * Aura P2 — data layer for /v1/content/aura-articles (owner GO 10-10-2026).
 * suggest: the top 3 keyword article cards for ONE site + a single-use confirm
 * token (held in memory only, never logged or stored). write: ONE confirmed card
 * per call (generation can take a minute; one call per card avoids timeouts).
 * Session, CSRF, the idempotency header and workspace scope are added by lib/api
 * and enforced by the server.
 * -------------------------------------------------------------------------- */

import { api } from '@/lib/api';
import type { BatchChannel } from '@/voice/actions/batchPublishModel';

// lib/api prefixes /v1 itself (F1 10-10-2026: a leading /v1 here became /v1/v1 -> 404).
const BASE = '/content/aura-articles';

export interface SuggestCard {
  index: number;
  keyword: string;
  position: number | null;
  volume: number | null;
  score: number | null;
  decision: 'new' | 'refresh';
  decision_reason: string;
  target_page: string | null;
  target_page_title: string;
  link_target?: string | null;
  in_inventory: boolean;
  working_title: string;
  outline: string[];
  flag: string;
  note: string;
}

export interface SuggestResponse {
  ok: boolean;
  kind?: 'suggest' | 'single';
  site: { id: number; label: string; url: string; name: string };
  project_id?: number;
  items: SuggestCard[];
  reason: string;
  spoken_summary: string;
  confirm_token?: string;
  expires_at?: string;
  write: { available: boolean; reason: string; message?: string; writable?: number; cap?: string; spent?: string; estimate_per_article_max?: string };
}

export interface WriteResponse {
  ok: boolean;
  index: number;
  keyword: string;
  decision: string;
  status: 'written' | 'duplicate' | 'not_synced' | 'failed' | string;
  reason: string;
  draft_id: number | null;
  title: string;
  model_calls: number;
  line: string;
  replayed?: boolean;
}

/** viewedProjectId: the domain in the header; the server refuses a site outside it. */
export function suggestArticles(targetId: number, channel: BatchChannel, viewedProjectId?: number | null): Promise<SuggestResponse> {
  return api.post<SuggestResponse>(`${BASE}/suggest`, viewedProjectId ? { target_id: targetId, project_id: viewedProjectId, channel } : { target_id: targetId, channel });
}

/** Single blog ("write a blog for <keyword>"): ONE card + read-back + token. No spend. */
export function proposeOneArticle(targetId: number, keyword: string, channel: BatchChannel, viewedProjectId?: number | null): Promise<SuggestResponse> {
  return api.post<SuggestResponse>(`${BASE}/propose-one`, viewedProjectId
    ? { target_id: targetId, project_id: viewedProjectId, keyword, channel }
    : { target_id: targetId, keyword, channel });
}

export function writeArticle(confirmToken: string, targetId: number, index: number, keyword: string): Promise<WriteResponse> {
  return api.post<WriteResponse>(`${BASE}/write`, { confirm_token: confirmToken, target_id: targetId, index, keyword });
}
